// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { DndContext } from "@dnd-kit/core";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Filter, Loader2, Search, UnfoldHorizontal, X } from "lucide-react";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { toast } from "sonner";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { CampaignsColumnSelector } from "@/components/campaigns-column-selector";
import { ColumnAliasModal } from "@/components/column-alias-modal";
import { CSVExportButton } from "@/components/csv-export-button";
import { DraggableTableHeader } from "@/components/draggable-table-header";
import { EntityFilterModal } from "@/components/entity-filter-modal";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import { FormulaColumnManager } from "@/components/formula-column-manager";
import ResetTableMenu from "@/components/reset-table-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { SortIcon } from "@/components/ui/sort-icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSavedViewContext } from "@/contexts/saved-view-context";
import {
  type CampaignMetrics,
  useCampaignMetrics as useCampaignMetricsAPI,
  useCampaignStatuses,
} from "@/hooks/api/use-campaign-metrics";
import {
  type Campaign,
  type CampaignWithMetrics,
  useCampaignsExport,
  useInfiniteCampaigns,
} from "@/hooks/api/use-campaigns";
import { useExternalConnectionLinks } from "@/hooks/api/use-external-connection-links";
import { useFieldDefinitions } from "@/hooks/api/use-field-definitions";
import { useMetricsDefinitions } from "@/hooks/api/use-metrics-definitions";
import {
  useOrganizationLocalStorage,
  useOrganizationSessionStorage,
} from "@/hooks/use-browser-storage";
import { useCampaignFilterState } from "@/hooks/use-campaign-filter-state";
import {
  useCampaignListFilters,
  useCampaignMetrics,
} from "@/hooks/use-campaign-list-filters";
import { useCampaignColumnSelection } from "@/hooks/use-column-selection";
import { usePaginatedCSVExport } from "@/hooks/use-csv-export";
import { useCampaignCustomFieldSelection } from "@/hooks/use-custom-field-selection";
import { useFormulaColumns } from "@/hooks/use-formula-columns";
import { useMetricCardFilters } from "@/hooks/use-metric-card-filters";
import { usePersistedColumnState } from "@/hooks/use-persisted-column-state";
import { useSavedViewState } from "@/hooks/use-saved-view-state";
import { useSearchState } from "@/hooks/use-search-state";
import {
  constrainedClosestCenter,
  restrictToTableBounds,
} from "@/lib/collision-detection";
import type { ColumnAliases } from "@/lib/column-aliases";
import {
  calculateColumnWidth,
  createBulkColumnResizeHandler,
  createSingleColumnResizeHandler,
} from "@/lib/column-resize-utils";
import { formatDateForFilename } from "@/lib/date-formatting";
import type { MetricsFilters } from "@/lib/filters";
import { cn } from "@/lib/utils";
import {
  DRILLDOWN_LIMIT,
  METRIC_CONFIG,
} from "@/pages/campaigns/campaign-explorer/campaign-metrics-config";
import { MetricDrilldown } from "@/pages/campaigns/campaign-explorer/metric-drilldown";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { getCampaignColumnDefs, getCampaignColumns } from "./campaign-columns";

export function CampaignsDataTable() {
  const CAMPAIGN_METRIC_LIMIT = 20000;

  // Use the sidebar hook to get state
  const { state, isMobile } = useSidebar();

  // Get loaded view ID for saved views
  const { loadedViewId, isLoadingView } = useSavedViewContext();
  const { resetToCurrentView, resetSpecificKeys } = useSavedViewState({
    pageType: "campaigns::list",
  });

  // State for sorting and searching - Replace useState search with sessionStorage
  const [sortConfig, setSortConfigState] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: "start_date",
    direction: "desc",
  });

  // sortConfig uses local state, not persisted storage
  const setSortConfig = setSortConfigState;

  // Use sessionStorage for search state (tab-specific)
  const {
    searchInputValue,
    debouncedSearchQuery,
    handleSearchChange,
    clearSearch,
  } = useSearchState("campaigns::list");

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showPerformanceMessage, setShowPerformanceMessage] = useState(false);
  const [selectedDrilldown, setSelectedDrilldown] = useState<{
    campaignId: string;
    metric: keyof CampaignMetrics;
  } | null>(null);

  // Track the previous sort config when it gets reset due to API warning
  const previousSortConfigRef = useRef<{
    key: string;
    direction: SortDirection;
  } | null>(null);

  // Ref to track if a metric sort was cleared by 'Clear All'
  const metricSortClearedRef = useRef<string | null>(null);

  // Loading refs for infinite scroll
  const { ref: loadMoreRef, inView } = useInView();

  // Structured filter groups (account / opportunity / touchpoint)
  const campaignFilterState = useCampaignFilterState("campaigns::list");
  const {
    accountFilters,
    opportunityFilters,
    touchpointFilters,
    hasAccountFilters,
    hasOpportunityFilters,
    hasTouchpointFilters,
    clearAllFilters: clearAllMetricFilters,
  } = campaignFilterState;

  // Total count of active metric filters (account + opportunity + touchpoint)
  const totalMetricFilterCount = useMemo(
    () =>
      accountFilters.filters.length +
      opportunityFilters.filters.length +
      touchpointFilters.filters.length,
    [accountFilters, opportunityFilters, touchpointFilters],
  );

  // Process metric filters for display using the same logic as Report Cards
  const {
    displayFilters: metricDisplayFilters,
    filterColumns: metricFilterColumns,
    handleClearFilter: handleClearMetricFilter,
  } = useMetricCardFilters(campaignFilterState);

  // Use campaign list filters hook
  const {
    filters,
    updateFilter,
    clearFilter,
    hasFilters,
    getDefaultSortSettings,
    getQueryParams,
  } = useCampaignListFilters();

  // Use the new campaign metrics hook
  const { selectedMetrics, setSelectedMetrics, getDefaultMetrics } =
    useCampaignMetrics();

  // Use custom field selection hook
  const { selectedFieldsList, fieldIdsParam } =
    useCampaignCustomFieldSelection();

  // Column aliases state management using sessionStorage (no longer persisted across sessions)
  const [columnAliases, setColumnAliases] =
    useOrganizationSessionStorage<ColumnAliases>(
      "campaigns::list::columns::aliases",
      {},
      {
        // Migrate from localStorage to sessionStorage
        migration: {
          from: {
            key: "campaigns::list::columns::aliases",
            storage: "localStorage",
            organizationScoped: true,
          },
          cleanup: true, // Remove from localStorage after migration
        },
      },
    );
  const [aliasModalOpen, setAliasModalOpen] = useState(false);
  const [aliasModalColumn, setAliasModalColumn] = useState<{
    id: string;
    originalTitle: string;
    originalDescription?: string;
    currentAlias?: string;
  } | null>(null);

  // Use formula columns hook (definitions only, no evaluation)
  const {
    formulaTableColumns,
    createFormulaColumn,
    updateFormulaColumn,
    deleteFormulaColumn,
  } = useFormulaColumns();

  // Add missing state variables
  const [isMetricLimitExceeded, setIsMetricLimitExceeded] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Reset sort to defaults
  const resetSortToDefaults = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });
  }, [getDefaultSortSettings, setSortConfig]);

  // Handle metric click to open drill-down
  const handleMetricClick = useCallback(
    (campaignId: string, metric: keyof CampaignMetrics) => {
      setSelectedDrilldown({ campaignId, metric });
    },
    [],
  );

  // Fetch unified field definitions for campaigns
  const { data: fieldDefs } = useFieldDefinitions("campaigns");

  // Fetch available metrics for formula system using the new hook
  const { data: availableMetrics } = useMetricsDefinitions("campaigns");

  // Derive select options for standard enum fields from field definitions
  const filterOptions = useMemo(() => {
    if (!fieldDefs) return undefined;

    // Build a map of enum values for all fields with enums
    const enumMap = fieldDefs.reduce<Record<string, string[]>>((acc, field) => {
      if (field.has_enum_values && field.enum_values) {
        acc[field.id] = field.enum_values;
      }
      return acc;
    }, {});

    return {
      type: enumMap.type || [],
      source_record_type: enumMap.source_record_type || [],
    } as const;
  }, [fieldDefs]);

  // Fetch external connection links
  const { data: connections, isLoading: isLoadingConnections } =
    useExternalConnectionLinks();

  // Define columns for the table, including metric columns and formula columns (moved here after all dependencies)
  const columns = useMemo<UnifiedTableColumn<Campaign>[]>(
    () =>
      getCampaignColumns(
        filterOptions,
        connections,
        isLoadingConnections,
        selectedMetrics,
        formulaTableColumns,
        selectedFieldsList,
        handleMetricClick,
      ),
    [
      connections,
      isLoadingConnections,
      filterOptions,
      selectedMetrics,
      formulaTableColumns,
      selectedFieldsList,
      handleMetricClick,
    ],
  );

  // Separate core columns from formula columns for column selection hook
  const coreColumns = useMemo(() => {
    return columns.filter((col) => !col.key.toString().startsWith("formula_"));
  }, [columns]);

  // Get column selection hook to control visibility (include formula columns for unified management)
  const {
    getVisibleColumnDefs,
    columnVisibility,
    setColumnVisibility,
    customFieldIdsParam: newCustomFieldIdsParam,
    selectedCustomColumns,
  } = useCampaignColumnSelection(
    coreColumns,
    selectedMetrics,
    formulaTableColumns,
  );

  // Reset column visibility to current view's saved configuration
  const resetColumnVisibility = useCallback(() => {
    resetSpecificKeys([
      "campaigns::list::metrics",
      "campaigns::list::custom-fields",
      "campaigns::list::column-visibility",
    ]);
    toast.success("Column visibility reset to saved view");
  }, [resetSpecificKeys]);

  const handleSelectMetrics = useCallback(
    (metrics: Array<keyof CampaignMetrics>) => {
      // Check if any removed metrics are currently used for sorting
      const removedMetrics = selectedMetrics.filter(
        (metric) => !metrics.includes(metric),
      );

      // Check if the current sort is on a metric that's being removed
      if (sortConfig.key.startsWith("metric_")) {
        const currentMetricKey = sortConfig.key.replace(
          "metric_",
          "",
        ) as keyof CampaignMetrics;

        if (removedMetrics.includes(currentMetricKey)) {
          // Find the metric title from the config
          const metricConfig = METRIC_CONFIG[currentMetricKey];
          const metricTitle = metricConfig?.title || currentMetricKey;

          // Show toast warning
          toast.warning("Table sort reset", {
            description: `Sort on ${metricTitle} has been removed because the column was deselected.`,
            duration: 8000,
          });

          // Reset sort to defaults
          resetSortToDefaults();
          clearFilter("sortColumn");
          clearFilter("sortDirection");
        }
      }

      setSelectedMetrics(metrics);
    },
    [
      selectedMetrics,
      sortConfig.key,
      setSelectedMetrics,
      resetSortToDefaults,
      clearFilter,
    ],
  );

  // Handle clear all filters and reset sort - Updated for sessionStorage
  const handleClearAllFilters = useCallback(() => {
    // Reset filters to current view's saved configuration
    resetSpecificKeys([
      "campaigns::list::filters",
      "campaigns::list::query",
      "campaigns::list::debounced",
    ]);
    toast.success("Table filters reset to saved view");
  }, [resetSpecificKeys]);

  // Track if user has ever visited campaigns list (persistent across sessions)
  const [hasVisitedBefore, setHasVisitedBefore] = useOrganizationLocalStorage(
    "campaigns::list::has-visited",
    false,
  );

  // Track first-time user state across effect reruns
  const hasAppliedDefaultsRef = useRef(false);

  // When component mounts, set search and sort from filters if they exist
  useEffect(() => {
    // Apply optimized defaults for first-time users
    const isFirstTimeUser = !hasAppliedDefaultsRef.current && !hasVisitedBefore;

    // Skip first-time user defaults if a saved view is loaded
    const shouldApplyFirstTimeDefaults = isFirstTimeUser && !loadedViewId;

    if (shouldApplyFirstTimeDefaults) {
      // First-time user detected AND no saved view is loaded - applying special defaults

      // Calculate date 6 months ago
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const formattedDate = sixMonthsAgo.toISOString().split("T")[0]; // Format as YYYY-MM-DD

      // Use a delayed execution to ensure sort settings are applied after initial rendering
      // This prevents race conditions where default sort might override our custom sort
      setTimeout(() => {
        // Safety check - only proceed if we haven't applied defaults yet and still no saved view
        if (hasAppliedDefaultsRef.current || loadedViewId) {
          return;
        }

        // Apply filters in specific order to ensure proper precedence:
        // 1. Clear any existing sort to prevent conflicts
        clearFilter("sortColumn");
        clearFilter("sortDirection");

        // 2. Set date filter to show only campaigns from the last 6 months
        updateFilter("start_date", { from: formattedDate });

        // 3. Set sort to Total Engagements (descending) for better data exploration
        updateFilter("sortColumn", "metric_total_engagements");
        updateFilter("sortDirection", "desc");

        setSortConfig({
          key: "metric_total_engagements",
          direction: "desc",
        });

        // Mark that we've applied defaults and user has visited
        hasAppliedDefaultsRef.current = true;
        setHasVisitedBefore(true);
      }, 100); // Delay ensures this runs after other effects complete
    } else if (isFirstTimeUser && loadedViewId) {
      // If it's a first-time user but they're loading a saved view,
      // still mark them as having visited so we don't apply defaults next time
      setHasVisitedBefore(true);
    }

    // Only set sort config from filters if NOT a first-time user (search is now in sessionStorage)
    if (!isFirstTimeUser) {
      // Set sort configuration from filters or use defaults
      if (filters.sortColumn) {
        // If sort is set in filters, use that
        setSortConfig({
          key: filters.sortColumn as string,
          direction: filters.sortDirection as SortDirection,
        });
      } else {
        // No sort in filters, use defaults
        const defaults = getDefaultSortSettings();
        setSortConfig({
          key: defaults.sortColumn,
          direction: defaults.sortDirection,
        });

        // When initializing with defaults and no sort in filters, add sort to filters
        // This ensures the API requests use the correct sort
        updateFilter("sortColumn", defaults.sortColumn);
        updateFilter("sortDirection", defaults.sortDirection);
      }
    }
  }, [
    filters.sortColumn,
    filters.sortDirection,
    getDefaultSortSettings,
    updateFilter,
    hasVisitedBefore,
    setHasVisitedBefore,
    clearFilter,
    setSortConfig,
    loadedViewId,
  ]);

  // Ref to track previously visible column IDs
  const prevActiveColumnsRef = useRef<string[]>([]);

  // Current visible column ids (core + custom + formula)
  const activeColumnIds = useMemo(() => {
    // Get current formula column IDs that exist
    const existingFormulaIds = new Set(
      formulaTableColumns.map((col) => `formula_${col.formulaColumn.id}`),
    );

    const visibleCore = Object.entries(columnVisibility)
      .filter(([, v]) => v)
      .map(([id]) => id)
      .filter((id) => {
        // Exclude formula columns that no longer exist in formulaTableColumns
        if (id.startsWith("formula_")) {
          return existingFormulaIds.has(id);
        }
        return true;
      });

    const customIds = selectedCustomColumns.map((c) => c.id);

    // Include formula column IDs that are both defined AND visible
    const formulaIds = formulaTableColumns
      .map((col) => `formula_${col.formulaColumn.id}`)
      .filter((formulaId) => columnVisibility[formulaId] === true); // Only include if explicitly visible

    return Array.from(new Set([...visibleCore, ...customIds, ...formulaIds]));
  }, [columnVisibility, selectedCustomColumns, formulaTableColumns]);

  // Get visible formula definitions (similar to selectedMetrics pattern)
  const selectedFormulas = useMemo(() => {
    const visibleFormulaIds = activeColumnIds
      .filter((id) => id.startsWith("formula_"))
      .map((id) => id.replace("formula_", "")); // Remove prefix to get formula ID

    return formulaTableColumns
      .filter((col) => visibleFormulaIds.includes(col.formulaColumn.id))
      .map((col) => ({
        id: col.formulaColumn.id,
        name: col.formulaColumn.name,
        formulaAST: col.formulaColumn.formulaAST,
        dataType: col.formulaColumn.dataType,
        description: col.formulaColumn.description,
      }));
  }, [formulaTableColumns, activeColumnIds]);

  // Persist column titles to use after removal
  const columnTitleLookupRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const lookup = columnTitleLookupRef.current;
    columns.forEach((col) => {
      const key = col.key.toString();
      if (!lookup[key]) {
        lookup[key] = col.title;
      }
    });
  }, [columns]);

  useEffect(() => {
    const prev = prevActiveColumnsRef.current;
    const removedIds = prev.filter((id) => !activeColumnIds.includes(id));

    if (removedIds.length > 0) {
      removedIds.forEach((id) => {
        if (filters[id] !== undefined) {
          clearFilter(id);
        }

        // Only reset sort if we're not currently loading a saved view
        if (sortConfig.key === id && !isLoadingView) {
          resetSortToDefaults();
          clearFilter("sortColumn");
          clearFilter("sortDirection");

          // Try to get column title from current columns first, then fallback to lookup
          let columnTitle = columnTitleLookupRef.current[id];

          if (!columnTitle) {
            // Try to find the column in the current columns array
            const column = columns.find((col) => col.key.toString() === id);
            columnTitle = column?.title || "";
          }

          if (columnTitle) {
            toast.warning("Table sort reset", {
              description: `Sort on ${columnTitle} has been removed because the column was deselected.`,
              duration: 8000,
            });
          }
        }
      });
    }

    prevActiveColumnsRef.current = activeColumnIds;
  }, [
    activeColumnIds,
    filters,
    sortConfig,
    clearFilter,
    resetSortToDefaults,
    columns,
    isLoadingView,
  ]);

  // Memoize deduplication of custom fields to avoid recreating on every render
  const uniqueCustomFields = useMemo(() => {
    return [
      ...selectedFieldsList,
      ...selectedCustomColumns
        .filter((column) => column.customField)
        .map((column) => column.customField!),
    ].reduce((map, field) => {
      map.set(field.id, field);
      return map;
    }, new Map<string, CustomFieldDefinition>());
  }, [selectedFieldsList, selectedCustomColumns]);

  // Helper function to build query parameters (shared between main query and CSV export)
  const buildQueryParams = useCallback(
    (limit: number) => {
      // Efficiently combine and deduplicate both field ID parameters
      const combinedCustomFieldIds = [
        ...(fieldIdsParam ? fieldIdsParam.split(",") : []),
        ...(newCustomFieldIdsParam ? newCustomFieldIdsParam.split(",") : []),
      ]
        .filter(Boolean) // Remove empty strings
        .filter((id, index, array) => array.indexOf(id) === index) // Deduplicate
        .join(",");

      const baseParams = {
        ...getQueryParams({
          searchTerm: debouncedSearchQuery,
          columns: columns, // Use full columns array including formula columns for sort validation
          limit,
          metrics: selectedMetrics,
          metric_filters: {
            ...(hasAccountFilters && {
              account_filters: accountFilters,
            }),
            ...(hasOpportunityFilters && {
              opportunity_filters: opportunityFilters,
            }),
            ...(hasTouchpointFilters && {
              touchpoint_filters: touchpointFilters,
            }),
          },
          formulas: selectedFormulas, // Use selectedFormulas (only visible formulas)
        }),
        extra_fields: combinedCustomFieldIds,
      };

      return baseParams;
    },
    [
      getQueryParams,
      debouncedSearchQuery,
      columns,
      selectedMetrics,
      accountFilters,
      opportunityFilters,
      touchpointFilters,
      hasAccountFilters,
      hasOpportunityFilters,
      hasTouchpointFilters,
      fieldIdsParam,
      newCustomFieldIdsParam,
      selectedFormulas,
    ],
  );

  // Get query parameters for API call
  const queryParams = useMemo(() => {
    return buildQueryParams(25);
  }, [buildQueryParams]);

  // Fetch campaigns with infinite scrolling
  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isError,
    error,
  } = useInfiniteCampaigns(queryParams);

  // Show performance message after 5 seconds of loading
  useEffect(() => {
    if (isLoading && selectedMetrics.length > 0) {
      const timer = setTimeout(() => {
        setShowPerformanceMessage(true);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    } else {
      setShowPerformanceMessage(false);
    }
  }, [isLoading, selectedMetrics.length]);

  // Function to display the sort reset toast notification
  const showSortResetToast = useCallback(
    (columnTitle: string, customDescription?: string) => {
      const description =
        customDescription ||
        `For performance reasons, the sort on ${columnTitle} has been removed. Please filter to fewer than ${CAMPAIGN_METRIC_LIMIT} campaigns and then try re-applying the sort.`;

      toast.warning("Table sort reset", {
        description,
        duration: 10000,
      });
    },
    [],
  );

  // Effect to show toast when a metric sort is cleared via 'Clear All', because 'Clear All' also resets to the default sort order anyway
  useEffect(() => {
    if (metricSortClearedRef.current) {
      const clearedMetricKey = metricSortClearedRef.current;
      // Find the column title using the key stored in the ref
      const column = columns.find((col) => col.key === clearedMetricKey);

      if (column?.title) {
        // Call the consolidated toast function with a non-performance description
        showSortResetToast(
          column.title,
          "Sort on metric has been removed because the column was deselected or table state was reset.",
        );
      }
      // Reset the ref after showing the toast
      metricSortClearedRef.current = null;
    }
  }, [columns, showSortResetToast]);

  // Track when a metric sort warning appears and reset sort (API driven)
  useEffect(() => {
    if (data?.pages[0]?.client_warning === "SORT_ON_METRIC_TOO_MANY_MATCHING") {
      // Store the current sort config before resetting it
      if (sortConfig.key.startsWith("metric_")) {
        previousSortConfigRef.current = { ...sortConfig };
        // Reset sort to default in the UI
        resetSortToDefaults();

        // Clear sort from filters
        clearFilter("sortColumn");
        clearFilter("sortDirection");

        // Find the column to get the title
        const column = columns.find(
          (col) => col.key === previousSortConfigRef.current?.key,
        );

        if (column?.title) {
          // Call the consolidated toast function
          showSortResetToast(column.title);
        }
      }
    }
  }, [
    data,
    sortConfig,
    resetSortToDefaults,
    clearFilter,
    columns,
    showSortResetToast,
  ]);

  // Setup for CSV export
  const { exportCampaignsAsync } = useCampaignsExport();

  // Flatten the pages of data into a single array
  const campaigns = useMemo(() => {
    const rawCampaigns = data?.pages.flatMap((page) => page.results) || [];
    return rawCampaigns;
  }, [data]);

  const {
    columnOrder,
    columnSizing,
    setColumnSizing,
    pinnedColumns,

    toggleColumnPin,
    wrappedColumns,
    setWrappedColumns,
    toggleColumnWrap,
    dndState: { sensors, handleDragEnd },
    resetToDefaultPinned,
    getInitializeColumnOrder,
  } = usePersistedColumnState("campaigns::list::columns", {
    getDefaultPinnedColumns: () => ["name"],
  });

  // Reset column widths to current view's saved configuration
  const resetColumnWidths = useCallback(() => {
    resetSpecificKeys(["campaigns::list::columns::sizes"]);
    toast.success("Column widths reset to saved view");
  }, [resetSpecificKeys]);

  // Reset column order to current view's saved configuration
  const resetColumnOrder = useCallback(() => {
    resetSpecificKeys(["campaigns::list::columns::order"]);
    toast.success("Column order reset to saved view");
  }, [resetSpecificKeys]);

  // Reset pinned columns - respects saved views if loaded
  const resetPinnedColumns = useCallback(() => {
    if (loadedViewId) {
      // Reset to saved view's configuration
      resetSpecificKeys([
        "campaigns::list::columns::pinned",
        "campaigns::list::columns::pinned-original-positions",
      ]);
      toast.success("Pinned columns reset to saved view");
    } else {
      // No saved view - reset to default pinned state
      resetToDefaultPinned();
    }
  }, [loadedViewId, resetSpecificKeys, resetToDefaultPinned]);

  // Reset wrapped columns
  const resetWrappedColumns = useCallback(() => {
    setWrappedColumns([]);
  }, [setWrappedColumns]);

  // Reset sort order to current view's saved configuration
  const resetSortOrder = useCallback(() => {
    resetSpecificKeys([
      "campaigns::list::filters", // This includes sortColumn and sortDirection
    ]);
    toast.success("Sort order reset to saved view");
  }, [resetSpecificKeys]);

  // Comprehensive table reset function
  const handleResetTable = useCallback(() => {
    // Reset to the current view's saved configuration
    // This will restore all settings to what they were when the view was loaded/saved
    resetToCurrentView();

    // Show success toast
    toast.success("Table reset to saved view configuration");
  }, [resetToCurrentView]);

  // Function to handle double-click on column resize handle to auto-size
  const handleDoubleClickResize = useCallback(
    (_: MouseEvent, columnId: string) => {
      const optimalWidth = calculateColumnWidth(
        columnId,
        "campaigns-table",
        {},
      );

      setColumnSizing((prev) => ({
        ...prev,
        [columnId]: optimalWidth,
      }));
    },
    [setColumnSizing],
  );

  // Single column resize handler for kebab menu
  const handleSingleColumnResize = useMemo(
    () => createSingleColumnResizeHandler(setColumnSizing, "campaigns-table"),
    [setColumnSizing],
  );

  // Bulk resize handler for autosize all button
  const handleBulkColumnResize = useMemo(
    () => createBulkColumnResizeHandler(setColumnSizing, "campaigns-table"),
    [setColumnSizing],
  );

  // Handle removing a column from view
  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      // Find the column to check its type
      const column = columns.find((col) => col.key === columnId);

      // For metric columns, remove from selectedMetrics
      if (column?.columnType === "metric") {
        // Extract metric key from column ID following our naming convention
        // Column IDs are prefixed for uniqueness: "metric_accounts_engaged"
        // But selectedMetrics state and API expect unprefixed keys: "accounts_engaged"
        const metricKey = columnId.replace(
          "metric_",
          "",
        ) as keyof CampaignMetrics;
        setSelectedMetrics((prev) => prev.filter((m) => m !== metricKey));
      } else {
        // For all other columns (core, custom, formula), update visibility
        setColumnVisibility((prev) => ({
          ...prev,
          [columnId]: false,
        }));
      }
    },
    [columns, setColumnVisibility, setSelectedMetrics],
  );

  // Column alias handlers
  const handleSetAlias = useCallback(
    (columnId: string) => {
      // Find the column to get its original title from the unified columns
      const column = columns.find((col) => col.key.toString() === columnId);

      if (column) {
        // For metric columns, get the description from METRIC_CONFIG
        let description: string | undefined;
        if (columnId.startsWith("metric_")) {
          const metricKey = columnId.replace("metric_", "");
          const metricConfig =
            METRIC_CONFIG[metricKey as keyof typeof METRIC_CONFIG];
          description = metricConfig?.tooltip;
        }

        setAliasModalColumn({
          id: columnId,
          originalTitle: column.title,
          originalDescription: description,
          currentAlias: columnAliases[columnId],
        });
        setAliasModalOpen(true);
      }
    },
    [columnAliases, columns],
  );

  const handleSaveAlias = useCallback(
    (columnId: string, alias: string) => {
      setColumnAliases((prev) => ({ ...prev, [columnId]: alias }));
    },
    [setColumnAliases],
  );

  const handleClearAlias = useCallback(
    (columnId: string) => {
      setColumnAliases((prev) => {
        const updated = { ...prev };
        delete updated[columnId];
        return updated;
      });
    },
    [setColumnAliases],
  );

  const handleResetAliases = useCallback(() => {
    resetSpecificKeys(["campaigns::list::columns::aliases"]);
    toast.success("Column aliases reset to saved view");
  }, [resetSpecificKeys]);

  // Convert to TanStack columnDefs with resizing enabled
  const columnDefs = useMemo<ColumnDef<Campaign>[]>(() => {
    // Get column definitions with both formula columns and custom fields
    const allDefs = getCampaignColumnDefs(
      filterOptions,
      connections,
      isLoadingConnections,
      selectedMetrics,
      formulaTableColumns,
      Array.from(uniqueCustomFields.values()),
      handleMetricClick,
    );

    // Add resizing capability and filter to only show visible columns
    return getVisibleColumnDefs(
      allDefs.map((col) => ({ ...col, enableResizing: true })),
    );
  }, [
    filterOptions,
    uniqueCustomFields,
    getVisibleColumnDefs,
    connections,
    isLoadingConnections,
    selectedMetrics,
    formulaTableColumns,
    handleMetricClick,
  ]);

  // Initialize TanStack Table
  const table = useReactTable({
    data: campaigns,
    columns: columnDefs,
    state: {
      columnSizing,
      columnOrder,
    },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 80,
      size: 150,
    },
  });

  // Initialize column order when columnDefs change
  useEffect(() => {
    const initializeOrder = getInitializeColumnOrder(columnDefs);
    initializeOrder();
  }, [columnDefs, getInitializeColumnOrder]);

  // Update total count and metric limit status when data changes
  useEffect(() => {
    if (data?.pages[0]) {
      const count = data.pages[0].total || 0;
      setTotalCount(count);
      setIsMetricLimitExceeded(count > CAMPAIGN_METRIC_LIMIT);
    }
  }, [data]);

  // Load more data when the load more element is in view
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Search is now managed by useSearchState hook - no need for manual debouncing

  // Handle sorting
  const handleSort = async (colId: string) => {
    // Find the original UnifiedTableColumn definition for default sort direction
    const unifiedColumn = columns.find((col) => col.key.toString() === colId);
    const sortable = unifiedColumn?.sortable;

    if (!sortable) return;

    const isMetricColumn = colId.startsWith("metric_");
    const isFormulaColumn = colId.startsWith("formula_");

    let direction: SortDirection = unifiedColumn?.defaultSortDirection || "asc";

    if (sortConfig.key === colId) {
      // Toggle between asc and desc only
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }

    if (isFormulaColumn) {
      // FORMULA COLUMN SORTING: Use server-side sorting like other columns

      // Disable sorting on formula columns when dataset is too large
      if (isMetricLimitExceeded) {
        return;
      }

      try {
        // Update filters state for server-side sorting
        updateFilter("sortColumn", colId);
        updateFilter("sortDirection", direction);

        // Update UI state for sort icons
        setSortConfig({ key: colId, direction });
      } catch (error: unknown) {
        console.error("Formula column sorting failed:", error);
        // Graceful fallback - reset sort to default
        const defaults = getDefaultSortSettings();
        setSortConfig({
          key: defaults.sortColumn,
          direction: defaults.sortDirection,
        });
        // Show error toast
        toast.error("Sorting failed", {
          description:
            "Failed to apply formula column sorting. Please try again.",
        });
      }
    } else {
      // NON-FORMULA COLUMN SORTING: Use server-side sorting

      // Skip sorting on metric columns when metric limit is exceeded
      if (isMetricColumn && isMetricLimitExceeded) return;

      // Update filters state for server-side sorting
      updateFilter("sortColumn", colId);
      updateFilter("sortDirection", direction);

      // Update UI state for sort icons
      setSortConfig({ key: colId, direction });
    }
  };

  // Search functions are now provided by useSearchState hook

  // Render sort icon
  const renderSortIcon = useCallback(
    (props: { key: string; sortable?: boolean }) => {
      if (!props.sortable) return null;

      // Check if this is a metric or formula column and if sorting is disabled due to limit
      const isMetricColumn = props.key.startsWith("metric_");
      const isFormulaColumn = props.key.startsWith("formula_");

      // For metric and formula columns, hide icon if limit exceeded
      if ((isMetricColumn || isFormulaColumn) && isMetricLimitExceeded) {
        return null;
      }

      return (
        <SortIcon
          isActive={sortConfig.key === props.key}
          direction={sortConfig.key === props.key ? sortConfig.direction : null}
          showOnHover={false} // Don't show the icon on hover, only when active
        />
      );
    },
    [sortConfig, isMetricLimitExceeded],
  );

  // First, let's create a helper function to define the tooltipText to be used in both places
  const getMetricLimitTooltipText = () => {
    return `Apply additional filters to sort on metrics or formula columns. For performance reasons, sorting on metrics and formula columns is currently disabled when more than ${CAMPAIGN_METRIC_LIMIT} campaigns match filters.`;
  };

  // Render loading state
  const renderLoadingState = () => {
    // Using columnDefs now for structure during loading
    const headerGroups = table.getHeaderGroups();

    return (
      <>
        <TableHeader>
          {headerGroups.map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted/50">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  <Skeleton className="h-4 w-full" />
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="bg-card">
          {Array.from({ length: 7 }).map((_, i) => (
            <TableRow key={i}>
              {i === 3 && showPerformanceMessage ? (
                <TableCell
                  colSpan={headerGroups[0].headers.length}
                  className="py-6"
                >
                  <div className="flex items-center">
                    <div
                      role="status"
                      aria-live="polite"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-muted/50 border border-muted-foreground/10 animate-in fade-in duration-500"
                    >
                      <svg
                        className="w-4 h-4 text-primary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-muted-foreground">
                        To speed up query time, try adding metric filters or
                        reducing the number of metric columns
                      </span>
                    </div>
                  </div>
                </TableCell>
              ) : (
                headerGroups[0].headers.map((header) => (
                  <TableCell key={`${i}-${header.id}`}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))
              )}
            </TableRow>
          ))}
        </TableBody>
      </>
    );
  };

  // Generate export button tooltip based on conditions
  const exportTooltipText = useMemo(() => {
    if (isMetricLimitExceeded && selectedMetrics.length > 0) {
      return `To maintain performance, exporting metrics for more than ${CAMPAIGN_METRIC_LIMIT} campaigns is not currently supported. Please filter to less than ${CAMPAIGN_METRIC_LIMIT} campaigns, or remove metric columns in order to export`;
    }
    return "Export campaigns data to CSV";
  }, [isMetricLimitExceeded, selectedMetrics.length]);

  // Memoize pinnedColumnPositions calculation at component level to avoid React hooks rules violation
  const pinnedColumnPositions = useMemo(() => {
    const positions: Record<string, number> = {};
    let cumulativeLeft = 0;

    columnOrder.forEach((colId) => {
      if (pinnedColumns.includes(colId)) {
        positions[colId] = cumulativeLeft;
        const column = table.getColumn(colId);
        if (column) {
          cumulativeLeft += column.getSize();
        }
      }
    });

    return positions;
  }, [columnOrder, pinnedColumns, table]);

  // Render table content (headers, body, etc.) without placing DndContext inside thead
  const renderTableContent = () => {
    const rowModel = table.getRowModel();

    return (
      <>
        <DraggableTableHeader
          table={table}
          columnOrder={columnOrder}
          handleSort={handleSort}
          sortConfig={sortConfig}
          renderSortIcon={renderSortIcon}
          filters={filters}
          updateFilter={updateFilter}
          clearFilter={clearFilter}
          columns={columns}
          handleDoubleClickResize={handleDoubleClickResize}
          isMetricLimitExceeded={isMetricLimitExceeded}
          getMetricLimitTooltipText={getMetricLimitTooltipText}
          pinnedColumns={pinnedColumns}
          onTogglePin={toggleColumnPin}
          wrappedColumns={wrappedColumns}
          onToggleWrap={toggleColumnWrap}
          onResizeToFit={handleSingleColumnResize}
          onRemoveColumn={handleRemoveColumn}
          columnAliases={columnAliases}
          onSetAlias={handleSetAlias}
        />
        <TableBody className="bg-card">
          {isError ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                Error loading campaigns: {error.message}
              </TableCell>
            </TableRow>
          ) : campaigns.length === 0 && !isLoading ? (
            <TableRow>
              <TableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-center text-muted-foreground"
              >
                No matching campaigns.
              </TableCell>
            </TableRow>
          ) : (
            <>
              {rowModel.rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const isPinned = pinnedColumns.includes(cell.column.id);
                    const isWrapped = wrappedColumns.includes(cell.column.id);
                    const pinnedLeft = pinnedColumnPositions[cell.column.id];

                    return (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                          ...(isPinned && { left: `${pinnedLeft}px` }),
                        }}
                        className={cn(
                          cell.column.id === sortConfig.key && "bg-muted/40",
                          isPinned && "pinned-column bg-background",
                        )}
                        data-column-id={cell.column.id}
                      >
                        <div
                          className={cn("cell-content", isWrapped && "wrapped")}
                          title={String(row.getValue(cell.column.id) || "")}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

              {/* Load more trigger element */}
              <TableRow ref={loadMoreRef}>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className={cn("h-12", !hasNextPage && "hidden")}
                >
                  {!isFetchingNextPage && hasNextPage ? "Load more" : null}
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </>
    );
  };

  // Check if the current sort is on a metric column that's been removed
  useEffect(() => {
    if (
      sortConfig.key.startsWith("metric_") &&
      !selectedMetrics.includes(
        sortConfig.key.replace("metric_", "") as keyof CampaignMetrics,
      )
    ) {
      // Reset sort to defaults if sorting on a metric that was removed
      resetSortToDefaults();

      // Also clear the sort from filters
      clearFilter("sortColumn");
      clearFilter("sortDirection");
    }
  }, [selectedMetrics, sortConfig.key, resetSortToDefaults, clearFilter]);

  // Create a filtered columns array that excludes invisible formula columns but keeps all other columns
  const exportColumns = useMemo(() => {
    const visibleFormulaColumnIds = new Set(
      Object.entries(columnVisibility)
        .filter(([id, isVisible]) => id.startsWith("formula_") && isVisible)
        .map(([id]) => id),
    );

    return columns.filter((col) => {
      const columnId = col.key.toString();
      // If it's a formula column, only include if visible
      if (columnId.startsWith("formula_")) {
        return visibleFormulaColumnIds.has(columnId);
      }
      // For all other columns (core, metrics, custom fields), always include
      return true;
    });
  }, [columns, columnVisibility]);

  // Set up paginated CSV export
  const { exportData, isExporting, defaultFilename } =
    usePaginatedCSVExport<CampaignWithMetrics>({
      fetchAllData: async () => {
        // Use the same parameter building logic as the main query
        const baseParams = buildQueryParams(25);

        // Add metrics and custom fields separately with correct typing
        return exportCampaignsAsync(baseParams);
      },
      columns: exportColumns,
      filename: `campaigns_${formatDateForFilename(new Date())}`,
    });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  // Determine if export should be disabled
  const isExportDisabled = useMemo(() => {
    return (
      isExporting ||
      isLoading ||
      campaigns.length === 0 ||
      (isMetricLimitExceeded && selectedMetrics.length > 0)
    );
  }, [
    isExporting,
    isLoading,
    campaigns.length,
    isMetricLimitExceeded,
    selectedMetrics.length,
  ]);

  return (
    <Card className="h-full relative flex flex-col">
      {/* Fixed position loading indicator */}
      {isFetchingNextPage && (
        <div className="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-10 bg-background/90 shadow-md rounded-md px-4 py-2 border">
          <div className="flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="ml-2">Loading more...</span>
          </div>
        </div>
      )}

      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter by campaign name..."
              className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
              onChange={(e) => handleSearchChange(e.target.value)}
              value={searchInputValue}
            />
            {searchInputValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={clearSearch}
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
              </Button>
            )}
          </div>
          <InfoTooltip
            trigger={
              <button
                className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Wildcard search info"
                onClick={(e) => e.stopPropagation()}
              >
                <InfoCircledIcon className="h-3.5 w-3.5" />
              </button>
            }
            side="right"
            sideOffset={5}
          >
            <div className="text-sm space-y-2">
              <p className="font-medium">Supports wildcards</p>
              <ul className="space-y-1">
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    _
                  </code>{" "}
                  matches a single character
                </li>
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    %
                  </code>{" "}
                  matches zero or more characters
                </li>
              </ul>
            </div>
          </InfoTooltip>
        </div>
        <div className="flex items-center gap-2">
          {/* Record count - moved to be first in right-side button group */}
          <div className="text-sm text-muted-foreground">
            {isLoading
              ? "Loading..."
              : totalCount > 0
                ? `${totalCount.toLocaleString()} matching campaigns`
                : "No matching campaigns"}
          </div>

          {/* Unified Campaigns Column & Metrics Selector */}
          <CampaignsColumnSelector
            selectedMetrics={selectedMetrics}
            onSelectMetrics={handleSelectMetrics}
            showMetricsSection={true}
            defaultMetrics={getDefaultMetrics()}
          />

          {/* Metric Filters Button - Only show when metrics are selected */}
          {selectedMetrics.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-1.5"
            >
              <Filter
                className={`h-4 w-4 ${
                  hasAccountFilters || hasOpportunityFilters
                    ? "text-primary"
                    : "text-muted-foreground"
                }`}
              />
              <span>Metric Filters</span>
              {totalMetricFilterCount > 0 && (
                <Badge className="ml-1 bg-primary text-primary-foreground">
                  {totalMetricFilterCount}
                </Badge>
              )}
            </Button>
          )}

          {/* Formula Column Manager */}
          <FormulaColumnManager
            formulaColumns={formulaTableColumns.map((col) => col.formulaColumn)}
            visibleColumnIds={Object.entries(columnVisibility)
              .filter(([, isVisible]) => isVisible)
              .map(([id]) => id)}
            fieldDefs={fieldDefs}
            availableMetrics={availableMetrics}
            onCreateColumn={(columnData) => {
              const result = createFormulaColumn(columnData);

              // Make the new formula column visible
              const newColumnId = `formula_${result.id}`;
              setColumnVisibility((prev) => ({
                ...prev,
                [newColumnId]: true,
              }));

              return result;
            }}
            onUpdateColumn={(id, updates) => {
              updateFormulaColumn(id, updates);

              // Ensure the updated formula column is visible
              const columnId = `formula_${id}`;
              setColumnVisibility((prev) => ({
                ...prev,
                [columnId]: true,
              }));
            }}
            onDeleteColumn={(id) => {
              // Just delete the column - the main effect will handle sort reset if needed
              deleteFormulaColumn(id);
            }}
            onToggleColumnVisibility={(columnId) => {
              // Just toggle column visibility - the main effect will handle sort reset if needed
              setColumnVisibility((prev) => ({
                ...prev,
                [columnId]: !prev[columnId],
              }));
            }}
            className="h-8"
          />

          {/* Autosize All Columns Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  const visibleColumnIds = columnOrder.filter(
                    (columnId) => columnVisibility[columnId] !== false,
                  );
                  handleBulkColumnResize(visibleColumnIds);
                }}
                className="h-8 w-8"
              >
                <UnfoldHorizontal className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Resize all visible columns to fit their content</p>
            </TooltipContent>
          </Tooltip>

          {/* Reset Table Menu */}
          <ResetTableMenu
            onResetWidths={resetColumnWidths}
            onResetOrder={resetColumnOrder}
            onResetColumns={resetColumnVisibility}
            onResetTableFilters={handleClearAllFilters}
            onResetMetricFilters={() => {
              resetSpecificKeys([
                "campaigns::filters::account",
                "campaigns::filters::opportunity",
                "campaigns::filters::touchpoint",
              ]);
              toast.success("Metric filters reset to saved view");
            }}
            onResetPinnedColumns={resetPinnedColumns}
            onResetSort={resetSortOrder}
            onResetAliases={handleResetAliases}
            onResetWrappedColumns={resetWrappedColumns}
            onResetAll={handleResetTable}
          />

          {/* CSV Export Button */}
          <ExportFilenameDialog
            open={isExportDialogOpen}
            onOpenChange={setIsExportDialogOpen}
            defaultFilename={defaultFilename}
            extension="csv"
            onExport={handleExport}
            disabled={isExportDisabled}
          >
            <CSVExportButton
              onClick={() => setIsExportDialogOpen(true)}
              isExporting={isExporting}
              disabled={isExportDisabled}
              tooltipText={exportTooltipText}
            />
          </ExportFilenameDialog>
        </div>
      </div>

      {/* Filter Modal */}
      <EntityFilterModal
        open={showFilterModal}
        onOpenChange={setShowFilterModal}
        scope="campaigns::list"
        entity="campaign"
      />

      {/* Column Alias Modal */}
      {aliasModalColumn && (
        <ColumnAliasModal
          open={aliasModalOpen}
          onOpenChange={setAliasModalOpen}
          columnId={aliasModalColumn.id}
          originalTitle={aliasModalColumn.originalTitle}
          originalDescription={aliasModalColumn.originalDescription}
          currentAlias={aliasModalColumn.currentAlias}
          onSave={handleSaveAlias}
          onClear={handleClearAlias}
        />
      )}

      {/* Active Filters Display - Show both table-level and metric filters */}
      <div className="px-4 pb-2 space-y-2 min-h-8 flex-shrink-0">
        {/* Table-level filters */}
        {hasFilters && (
          <div className="flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={
                // Filter out sort-related properties from display
                Object.fromEntries(
                  Object.entries(filters).filter(
                    ([key]) => key !== "sortColumn" && key !== "sortDirection",
                  ),
                )
              }
              columns={columns}
              clearFilter={clearFilter}
              clearAllFilters={handleClearAllFilters}
              clearAllText="Clear all table filters"
              filterType="table"
            />
          </div>
        )}

        {/* Metric filters */}
        {Object.keys(metricDisplayFilters).length > 0 && (
          <div className="flex items-center justify-between">
            <ActiveFiltersDisplay
              filters={metricDisplayFilters}
              columns={metricFilterColumns}
              clearFilter={handleClearMetricFilter}
              clearAllFilters={clearAllMetricFilters}
              clearAllText="Clear all metric filters"
              filterType="metric"
            />
          </div>
        )}
      </div>

      {/* Main Table Container - Add class when resizing */}
      <DndContext
        sensors={sensors}
        collisionDetection={constrainedClosestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToTableBounds]}
      >
        <div
          ref={tableContainerRef}
          className={cn(
            "rounded-md border-t border-x overflow-y-auto flex-grow",
            table.getState().columnSizingInfo.isResizingColumn &&
              "table-resizing",
          )}
          style={{
            maxWidth: isMobile
              ? "calc(100vw - 2rem)"
              : state === "collapsed"
                ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                : "calc(100vw - var(--sidebar-width) - 2rem)",
            minWidth: "640px", // Prevent table from becoming too narrow on small screens
          }}
        >
          <Table
            className="w-full table-fixed"
            style={{ width: table.getTotalSize() }}
            data-table-id="campaigns-table"
            data-table-container
          >
            {isLoading ? renderLoadingState() : renderTableContent()}
          </Table>
        </div>
      </DndContext>

      {/* Metric Drilldown Modal */}
      {selectedDrilldown && (
        <MetricDrilldownWithStatuses
          campaignId={selectedDrilldown.campaignId}
          metric={selectedDrilldown.metric}
          campaigns={campaigns}
          onClose={() => setSelectedDrilldown(null)}
          filters={{
            ...(hasAccountFilters && {
              account_filters: accountFilters,
            }),
            ...(hasOpportunityFilters && {
              opportunity_filters: opportunityFilters,
            }),
            ...(hasTouchpointFilters && {
              touchpoint_filters: touchpointFilters,
            }),
          }}
        />
      )}
    </Card>
  );
}

// Mapping from derived metrics to their base metrics
const METRIC_TO_BASE: Record<string, keyof CampaignMetrics> = {
  // Amount metrics map to their count equivalents
  opportunities_influenced_amount: "opportunities_influenced",
  opportunities_influenced_in_cycle_amount: "opportunities_influenced_in_cycle",
  opportunities_created_post_engagement_amount:
    "opportunities_created_post_engagement",
  progressed_opportunities_amount: "progressed_opportunities",
  opportunities_influenced_won_amount: "opportunities_influenced_won",
  sourced_opportunities_amount: "sourced_opportunities",
  // Per-entity metrics map to their entity totals
  engagements_per_person: "people_engaged",
  engagements_per_account: "accounts_engaged",
  engagements_per_deal_contact: "deal_contacts_engaged",
};

// Related metrics to fetch for compact card display
const RELATED_METRICS_MAP: Record<string, Array<keyof CampaignMetrics>> = {
  // Amount metrics for opportunity-related metrics
  opportunities_influenced: ["opportunities_influenced_amount"],
  opportunities_influenced_won: ["opportunities_influenced_won_amount"],
  opportunities_influenced_in_cycle: [
    "opportunities_influenced_in_cycle_amount",
  ],
  opportunities_created_post_engagement: [
    "opportunities_created_post_engagement_amount",
  ],
  progressed_opportunities: ["progressed_opportunities_amount"],
  sourced_opportunities: ["sourced_opportunities_amount"],
  // Per-entity metrics for engagement metrics
  accounts_engaged: ["engagements_per_account"],
  people_engaged: ["engagements_per_person"],
  deal_contacts_engaged: ["engagements_per_deal_contact"],
};

// Wrapper component to handle status fetching for campaigns that need it
function MetricDrilldownWithStatuses({
  campaignId,
  metric,
  campaigns,
  onClose,
  filters,
}: {
  campaignId: string;
  metric: keyof CampaignMetrics;
  campaigns: Campaign[];
  onClose: () => void;
  filters: MetricsFilters;
}) {
  const campaign = campaigns.find((c) => c.id === campaignId);
  const SOURCES_THAT_DONT_REQUIRE_STATUS = new Set(["hubspot.email_campaign"]);
  const requiresStatus = !SOURCES_THAT_DONT_REQUIRE_STATUS.has(
    campaign?.source_record_type || "",
  );

  // Fetch statuses if the campaign requires them
  const { data: statusData } = useCampaignStatuses(
    requiresStatus ? campaignId : undefined,
  );

  // Get all statuses that represent engagement
  const engagementStatuses = useMemo(() => {
    if (!requiresStatus) return [];
    if (!statusData) return [];

    return statusData.statuses
      .filter((s) => s.represents_engagement)
      .map((s) => s.status);
  }, [requiresStatus, statusData]);

  // Map derived metrics to their base metrics for drill-down
  const drilldownMetric = METRIC_TO_BASE[metric] || metric;

  // Define metrics to fetch for the compact card
  // Include the clicked metric plus commonly related metrics to ensure compact card displays properly
  const metricsToFetch: (keyof CampaignMetrics)[] = [
    drilldownMetric,
    ...(RELATED_METRICS_MAP[drilldownMetric] || []),
  ];

  // Fetch metrics for the compact card display
  const { data: metrics, loadingStates } = useCampaignMetricsAPI({
    campaignId,
    metrics: metricsToFetch,
    statuses: engagementStatuses,
    filters,
    enabled: true,
  });

  return (
    <MetricDrilldown
      open={true}
      onOpenChange={(open) => !open && onClose()}
      campaignId={campaignId}
      metric={drilldownMetric}
      statuses={engagementStatuses}
      filters={filters}
      metrics={metrics}
      loadingStates={loadingStates}
      drillDownLimit={DRILLDOWN_LIMIT}
    />
  );
}
